// @tennis-matches/services/tournament-wizard/validation
// Configuration-based validation rules

import { validators } from "./validators.js";
import { __asStr } from "@tennis-matches/core/utils";

/**
 * Create validation rules based on configuration
 * @param {Object} config - Validation configuration
 * @param {Function} t - Optional translation function
 * @returns {Object} Validation rules object
 */
export function createValidationRules(config, t = null) {
  const { players = {}, features = {}, modes = {} } = config;

  return {
    /**
     * Validate players step
     */
    validatePlayers: (setup) => {
      const { format, playerCount, names } = setup;
      const minPlayers =
        players?.minPlayers?.[format] || players?.minPlayers?.default || 4;

      const results = [];
      const warnings = [];

      // Min players check
      const minCheck = validators.minPlayers(playerCount, minPlayers, format);
      if (!minCheck.ok) {
        results.push(minCheck.message);
      }

      // Unique names check
      const uniqueCheck = validators.uniqueNames(names, t);
      if (!uniqueCheck.ok) {
        results.push(uniqueCheck.message);
      }

      // Player count range warning (if configured)
      if (players?.warnRange) {
        const rangeCheck = validators.playerCountRange(
          playerCount,
          players.warnRange.min,
          players.warnRange.max,
        );
        if (rangeCheck.message) {
          warnings.push(rangeCheck.message);
        }
      }

      return {
        ok: results.length === 0,
        messages: results,
        warnings,
      };
    },

    /**
     * Validate time step
     */
    validateTime: (setup) => {
      const {
        startDate,
        startTime,
        minutesPerRound,
        totalMinutes,
        courtCount,
        courtNumbers,
      } = setup;
      const results = [];

      // Required fields
      const timeFormatCheck = validators.timeFormat(startTime);
      if (!timeFormatCheck.ok) {
        results.push(timeFormatCheck.message);
      }

      const startPastCheck = validators.startNotInPast(startDate, startTime, t);
      if (!startPastCheck.ok) {
        results.push(startPastCheck.message);
      }

      // Positive numbers
      const minutesCheck = validators.positiveNumber(
        minutesPerRound,
        "Minuten pro Runde",
      );
      if (!minutesCheck.ok) {
        results.push(minutesCheck.message);
      }

      const totalCheck = validators.positiveNumber(totalMinutes, "Gesamtdauer");
      if (!totalCheck.ok) {
        results.push(totalCheck.message);
      }

      const courtsCheck = validators.positiveNumber(courtCount, "Platzanzahl");
      if (!courtsCheck.ok) {
        results.push(courtsCheck.message);
      }

      // Court numbers validation (expert mode only, if enabled)
      if (features?.expertMode && courtNumbers && courtCount > 0) {
        const courtCheck = validators.courtsValid(courtNumbers, courtCount, t);
        if (!courtCheck.ok) {
          results.push(courtCheck.message);
        }
      }

      return {
        ok: results.length === 0,
        messages: results,
        warnings: [],
      };
    },

    /**
     * Validate rules step
     */
    validateRules: (setup) => {
      const { winPoints, drawPoints } = setup;
      const results = [];

      const pointsCheck = validators.pointsValid(winPoints, drawPoints, t);
      if (!pointsCheck.ok) {
        results.push(pointsCheck.message);
      }

      return {
        ok: results.length === 0,
        messages: results,
        warnings: [],
      };
    },

    /**
     * Validate groupsKo step (Clubmeisterschaft)
     */
    validateGroupsKo: (setup) => {
      const { groupsKo = {}, playerCount = 0, tournamentMode, format } = setup;
      const entrants =
        format === "fixedDoubles"
          ? playerCount % 2 === 0
            ? Math.floor(playerCount / 2)
            : 0
          : playerCount;
      return validators.groupsKo(groupsKo, entrants, t, { tournamentMode });
    },

    validateKoOnly: (setup) => {
      const { koOnly = {}, playerCount = 0, format } = setup;
      const entrants =
        format === "fixedDoubles"
          ? playerCount % 2 === 0
            ? Math.floor(playerCount / 2)
            : 0
          : playerCount;
      return validators.koOnly(koOnly, entrants, t);
    },

    /**
     * Validate meta step
     */
    validateMeta: (setup) => {
      const { startDate, startTime, format } = setup;
      const results = [];

      if (!features?.skipMetaDateTime) {
        const dateCheck = validators.required(startDate, "Startdatum");
        if (!dateCheck.ok) {
          results.push(dateCheck.message);
        }

        const timeCheck = validators.timeFormat(startTime);
        if (!timeCheck.ok) {
          results.push(timeCheck.message);
        }

        const startPastCheck = validators.startNotInPast(
          startDate,
          startTime,
          t,
        );
        if (!startPastCheck.ok) {
          results.push(startPastCheck.message);
        }
      }

      const formatCheck = validators.required(format, "Spielformat");
      if (!formatCheck.ok) {
        results.push(formatCheck.message);
      }

      return {
        ok: results.length === 0,
        messages: results,
        warnings: [],
      };
    },
  };
}
